<template>
  <EquipmentRow :key="`${seal?.id}-${index}`">
    <template #left></template>
    <template #default="{ toggle }">
      <AddRemoveButtons
        :disable-add-above="disableAddAbove"
        :disable-add-below="disableAddBelow"
        :disable-remove="disableRemove"
        :hide-add-above="hideAddAbove"
        :hide-add-below="hideAddBelow"
        :hide-remove="hideRemove"
        @add-above="$emit('add-above')"
        @add-below="$emit('add-below')"
        @remove="$emit('remove', seal)"
      >
        <template #middle-content>
          <EquipmentPanel
            data-cy="hps-equipment-panel"
            class="w-full"
            :rule-names="
              index === 0
                ? [
                    'operating_seal_thrust_bearing_load',
                    'shut_in_seal_thrust_bearing_load',
                    'seal_size_top_casing',
                    'seal_size_bottom_casing',
                    'seal_shaft_limit_hs',
                    'seal_shaft_limit_std',
                    'seal_obsolete'
                  ]
                : [
                    'seal_size_top_casing',
                    'seal_size_bottom_casing',
                    'seal_shaft_limit_hs',
                    'seal_shaft_limit_std',
                    'seal_obsolete'
                  ]
            "
            :xref-id="seal.id"
            @toggle="toggle"
          >
            <template #header>Thrust Chamber Type</template>
            <template v-if="seal?.r_seal" #trigger>
              {{
                computeSealLabel(seal?.r_seal, seal?.use_high_strength_shaft)
              }}
            </template>
          </EquipmentPanel>
        </template>
      </AddRemoveButtons>
    </template>
    <template #expand="{ toggle }">
      <SealThrustChamberSearchView
        :is-admin="false"
        data-cy="hps-seal-search-view"
        @select="handleSealThrustChamberSelect(seal, $event, toggle)"
      />
    </template>
    <template #right>
      <div class="flex">
        <div class="mr-2">
          <ConverterInput
            label="Length"
            :model-value="seal?.r_seal?.length_inches"
            :base-unit="LengthUnits.Inches"
            :target-unit="getLargeLengthUnit('Length.convert.Inches')"
            :model="LargeLengthUnits.options()"
            mode="convert"
          />
        </div>
        <div
          class="field-checkbox align-items-center flex mb-0"
          data-cy="hps-checkbox-wrapper"
        >
          <AlertWrapper :rule-names="['seal_shaft_limit_std']">
            <Checkbox
              :model-value="seal?.use_high_strength_shaft || false"
              :disabled="!seal?.r_seal || !canEdit"
              :input-id="`high-strength-shaft-${seal?.id}`"
              binary
              data-cy="hps-high-strength-shaft-checkbox"
              @update:model-value="
                $emit('update:seals', {
                  ...seal,
                  use_high_strength_shaft: $event
                })
              "
            />
            <label
              :for="`high-strength-shaft-${seal?.id}`"
              :class="{ disabled: !seal?.r_seal }"
            >
              Use High Strength Shaft
            </label>
          </AlertWrapper>
        </div>
      </div>
    </template>
  </EquipmentRow>
</template>

<script setup lang="ts">
import type { PropType } from "vue";
import { provide, computed } from "vue";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { computeSealLabel } from "@/models/equipment/seals/properties";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import AlertWrapper from "@/components/AlertWrapper.vue";
import SealThrustChamberSearchView from "./SealThrustChamberSearchView.vue";
import type { XrefSeals } from "@/composables/forms/formTypes";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import {
  scenarioKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import type { Seals } from "gql/api";

const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);

const props = defineProps({
  seal: {
    type: Object as PropType<XrefSeals[number]>,
    default: null
  },
  index: {
    type: Number,
    default: -1
  },
  canEdit: {
    type: Boolean,
    default: true
  },
  hideAddAbove: {
    type: Boolean,
    default: false
  },
  hideAddBelow: {
    type: Boolean,
    default: false
  },
  hideRemove: {
    type: Boolean,
    default: false
  },
  disableAddAbove: {
    type: Boolean,
    default: false
  },
  disableAddBelow: {
    type: Boolean,
    default: false
  },
  disableRemove: {
    type: Boolean,
    default: false
  }
});

const $emit = defineEmits<{
  (e: "add-below"): void;
  (e: "add-above"): void;
  (e: "remove", seal: XrefSeals[number]): void;
  (e: "update:seals", seal: XrefSeals[number]): void;
}>();

function handleSealThrustChamberSelect(
  xrefSeal: XrefSeals[number],
  selectedSealThrustChamberEquipment: Seals,
  toggle: () => void
): void {
  toggle();
  $emit("update:seals", {
    ...xrefSeal,
    id: xrefSeal?.id,
    seal_id: selectedSealThrustChamberEquipment.id,
    r_seal: selectedSealThrustChamberEquipment
  });
}

provide(
  "selected-equipment-id",
  computed(() => props.seal.r_seal?.id)
);
</script>
